/**
 * Turaq.kg chat — общий файл backend/frontend (docs/chat/CONTRACT.md §9).
 *
 * Только чистые TypeScript-типы и константы, без импортов: frontend хранит
 * побайтовую копию этого файла. Владелец — backend
 * (src/modules/chat/chat.events.ts).
 */

export const CONTRACT_VERSION = 3;

/** Десятичное неотрицательное целое без ведущих нулей (bigint в БД). */
export type Seq = string;

export const CHAT_ERROR_CODES = [
  'CHAT_FORBIDDEN',
  'CHAT_LISTING_NOT_FOUND',
  'CHAT_OWN_LISTING',
  'CHAT_LISTING_UNAVAILABLE',
  'CHAT_MESSAGE_TOO_LONG',
  'CHAT_EMPTY_MESSAGE',
  'CHAT_RATE_LIMITED',
  'CHAT_EDIT_WINDOW_EXPIRED',
  'CHAT_DELETE_WINDOW_EXPIRED',
  'CHAT_NOT_EDITABLE',
  'CHAT_ATTACHMENT_INVALID',
  'CHAT_VALIDATION_FAILED',
  'CHAT_UNAUTHORIZED',
  'CHAT_INTERNAL',
  /** Больше 3 закреплённых диалогов (B14). */
  'CHAT_PIN_LIMIT',
] as const;

/** Сколько диалогов можно закрепить (B14). */
export const MAX_PINNED_CONVERSATIONS = 3;

export type ChatErrorCode = (typeof CHAT_ERROR_CODES)[number];

/** Тело REST-ошибки: существующий формат проекта плюс `code`. */
export interface ChatRestError {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string;
  error: string;
  code: ChatErrorCode;
}

export type MessageType = 'text' | 'image' | 'system';

export type ListingStatus = 'active' | 'sold' | 'reserved' | 'archived';

export interface ListingSummaryDto {
  id: number;
  title: string;
  /** Тот же тип, что у `price` в публичном API объявлений. */
  price: string;
  currency: string | null;
  coverUrl: string | null;
  status: ListingStatus;
}

export interface CounterpartDto {
  id: number;
  name: string;
  avatarUrl: string | null;
  isOnline: boolean;
  lastSeenAt: string | null;
  /** Официальный аккаунт Turaq.kg (синяя галочка, канал). */
  isOfficial: boolean;
  /** Проверенный продавец/компания (users.is_tick). */
  isVerified: boolean;
}

export interface AttachmentDto {
  id: string;
  width: number;
  height: number;
  blurhash: string | null;
  size: number;
  thumbUrl: string | null;
  displayUrl: string | null;
}

export interface ReplyPreviewDto {
  id: string;
  senderId: number | null;
  type: MessageType;
  bodyPreview: string | null;
  thumbUrl: string | null;
  deleted: boolean;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  seq: Seq;
  senderId: number | null;
  type: MessageType;
  body: string | null;
  replyTo: ReplyPreviewDto | null;
  attachments: AttachmentDto[];
  clientMsgId: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  /** Реакции (B15); `mine` — для того, кто получает DTO. */
  reactions: ReactionSummary[];
}

/** Одна реакция на сообщении: сколько человек и есть ли среди них я. */
export interface ReactionSummary {
  emoji: string;
  count: number;
  mine: boolean;
}

/**
 * Разрешённые реакции (B15) — QUICK_REACTIONS и MORE_REACTIONS фронтенда
 * (src/chat/components/message/MessageFocusOverlay.tsx).
 */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
export const MORE_REACTIONS = [
  '🔥',
  '👏',
  '🎉',
  '😍',
  '🤝',
  '💯',
  '✅',
  '👌',
  '🙌',
  '🥰',
  '😊',
  '😁',
  '🤔',
  '😅',
  '😎',
  '🤩',
  '😡',
  '👎',
  '🏡',
  '🔑',
  '💰',
  '📍',
  '⭐',
  '💪',
] as const;
export const ALLOWED_REACTIONS: readonly string[] = [
  ...QUICK_REACTIONS,
  ...MORE_REACTIONS,
];

/** direct — покупатель↔продавец; channel — канал Turaq.kg (только чтение и реакции). */
export type ConversationKind = 'direct' | 'channel';

/** Постоянный id виртуального диалога-канала Turaq.kg (B16). */
export const OFFICIAL_CHANNEL_ID = 'official';

export interface ConversationDto {
  id: string;
  kind: ConversationKind;
  /** null — объявление удалено. */
  listing: ListingSummaryDto | null;
  counterpart: CounterpartDto;
  lastMessage: MessageDto | null;
  unreadCount: number;
  updatedAt: string;
  myLastReadSeq: Seq;
  counterpartLastDeliveredSeq: Seq;
  counterpartLastReadSeq: Seq;
  /** Мои настройки диалога (B14); собеседник их не видит. */
  pinnedAt: string | null;
  archivedAt: string | null;
  /** «Удалить чат»: сообщения до этого seq для меня скрыты; null — не удаляли. */
  clearedUpToSeq: Seq | null;
  /** Чат поддержки с Turaq.kg (без объявления, POST /chat/conversations/support). */
  isSupport?: boolean;
  /**
   * Чат с профилем специалиста без объявления (POST /chat/conversations
   * { profile_id }): listing = null. У остальных диалогов поля нет.
   */
  profile?: ProfileSummaryDto | null;
}

export interface ProfileSummaryDto {
  id: number;
  name: string;
  avatarUrl: string | null;
}

/** Мои настройки диалога — ответ REST и событие conversation:prefs (B14). */
export interface ConversationPrefsDto {
  conversationId: string;
  pinnedAt: string | null;
  archivedAt: string | null;
  clearedUpToSeq: Seq | null;
}

/** Реакции сообщения изменились (B15); `mine` — для получателя события. */
export interface MessageReactionsDto {
  conversationId: string;
  messageId: string;
  reactions: ReactionSummary[];
}

export interface ReactionsResponseDto {
  reactions: ReactionSummary[];
}

export interface ConversationListDto {
  items: ConversationDto[];
  nextCursor: string | null;
}

export interface MessageListDto {
  items: MessageDto[];
  hasMore: boolean;
}

export interface UnreadCountDto {
  total: number;
}

// ── Socket.IO ──────────────────────────────────────────────────────────────

export const CHAT_NAMESPACE = '/chat';
export const CHAT_SOCKET_PATH = '/api/socket.io';

export const ClientEvents = {
  MESSAGE_SEND: 'message:send',
  MESSAGE_EDIT: 'message:edit',
  MESSAGE_DELETE: 'message:delete',
  MESSAGE_DELIVERED: 'message:delivered',
  CONVERSATION_READ: 'conversation:read',
  TYPING: 'typing',
} as const;

export const ServerEvents = {
  MESSAGE_NEW: 'message:new',
  MESSAGE_UPDATED: 'message:updated',
  MESSAGE_DELETED: 'message:deleted',
  MESSAGE_STATUS: 'message:status',
  TYPING: 'typing',
  PRESENCE: 'presence',
  CONVERSATION_UPDATED: 'conversation:updated',
  UNREAD_TOTAL: 'unread:total',
  CONVERSATION_PREFS: 'conversation:prefs',
  MESSAGE_REACTIONS: 'message:reactions',
} as const;

export type ChatAck<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: ChatErrorCode; message: string } };

export type DeleteScope = 'me' | 'everyone';

export interface MessageSendPayload {
  conversationId: string;
  clientMsgId: string;
  body?: string;
  attachmentIds?: string[];
  replyToId?: string;
}

export interface MessageEditPayload {
  messageId: string;
  body: string;
}

export interface MessageDeletePayload {
  messageId: string;
  scope: DeleteScope;
}

export interface MessageDeletedDto {
  id: string;
  conversationId: string;
  scope: DeleteScope;
}

export interface WatermarkPayload {
  conversationId: string;
  upToSeq: Seq;
}

export interface WatermarkAckDto {
  conversationId: string;
  deliveredUpToSeq: Seq;
  readUpToSeq: Seq;
}

export interface MessageStatusDto {
  conversationId: string;
  /** Чьи это watermarks. */
  userId: number;
  deliveredUpToSeq: Seq;
  readUpToSeq: Seq;
}

export interface TypingPayload {
  conversationId: string;
  on: boolean;
}

export interface TypingDto {
  conversationId: string;
  userId: number;
  on: boolean;
}

export interface PresenceDto {
  userId: number;
  isOnline: boolean;
  lastSeenAt: string | null;
}

export interface ClientToServerEvents {
  'message:send': (
    payload: MessageSendPayload,
    ack: (res: ChatAck<MessageDto>) => void,
  ) => void;
  'message:edit': (
    payload: MessageEditPayload,
    ack: (res: ChatAck<MessageDto>) => void,
  ) => void;
  'message:delete': (
    payload: MessageDeletePayload,
    ack: (res: ChatAck<MessageDeletedDto>) => void,
  ) => void;
  'message:delivered': (
    payload: WatermarkPayload,
    ack: (res: ChatAck<WatermarkAckDto>) => void,
  ) => void;
  'conversation:read': (
    payload: WatermarkPayload,
    ack: (res: ChatAck<WatermarkAckDto>) => void,
  ) => void;
  typing: (payload: TypingPayload, ack: (res: ChatAck<null>) => void) => void;
}

export interface ServerToClientEvents {
  'message:new': (payload: MessageDto) => void;
  'message:updated': (payload: MessageDto) => void;
  'message:deleted': (payload: MessageDeletedDto) => void;
  'message:status': (payload: MessageStatusDto) => void;
  typing: (payload: TypingDto) => void;
  presence: (payload: PresenceDto) => void;
  'conversation:updated': (payload: ConversationDto) => void;
  'unread:total': (payload: UnreadCountDto) => void;
  'conversation:prefs': (payload: ConversationPrefsDto) => void;
  'message:reactions': (payload: MessageReactionsDto) => void;
  /** Канал Turaq.kg в формате /api/official/* (docs/AI_OFFICIAL_API.md). */
  'official:new': (payload: OfficialMessageDto) => void;
  'official:updated': (payload: OfficialMessageDto) => void;
  'official:deleted': (payload: OfficialDeletedDto) => void;
  /** Центр уведомлений (шаг 4, docs/CRM.md): новое уведомление пользователю. */
  'notification:new': (payload: UserNotificationDto) => void;
}

/** Уведомление центра уведомлений (лид, задача, показ, бронь, отзыв). */
export interface UserNotificationDto {
  id: number;
  type: string;
  title: string;
  body: string | null;
  url: string | null;
  data: Record<string, unknown>;
  created_at: string;
  unread: number;
}

// ── Системные чаты: канал Turaq.kg и ИИ-жардамчы (snake_case, как в ТЗ) ────

/** Карточка объявления — одна для канала и ИИ-жардамчы. */
export interface ListingCardDto {
  id: number;
  url: string;
  photo_url: string | null;
  price: number | null;
  currency: string | null;
  /** «65 000 $» или «Келишимдик баа». */
  price_label: string;
  price_per_m2: number | null;
  category: 'apartment' | 'house' | 'land' | 'commercial';
  rooms: number | null;
  area_m2: number | null;
  land_sotka: number | null;
  floor: number | null;
  floors_total: number | null;
  region: string;
  district: string | null;
  address_short: string;
  owner_type: 'owner' | 'realtor';
  views: number;
  created_at: string;
}

export interface OfficialImageDto {
  url: string;
  width: number;
  height: number;
}

/** Пост канала Turaq.kg для /api/official/* и событий official:*. */
export interface OfficialMessageDto {
  id: string;
  seq: Seq;
  text: string | null;
  image_url: string | null;
  images: OfficialImageDto[];
  link_url: string | null;
  listings: ListingCardDto[];
  created_at: string;
  edited_at: string | null;
}

export interface OfficialDeletedDto {
  id: string;
}

/** Сравнение seq без BigInt: сначала длина, затем лексикографически. */
export function compareSeq(a: Seq, b: Seq): number {
  if (a.length !== b.length) return a.length < b.length ? -1 : 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

// ── Uploads (REST) ─────────────────────────────────────────────────────────

export interface PresignFile {
  name: string;
  mime: 'image/jpeg' | 'image/png' | 'image/webp';
  size: number;
}

export interface PresignRequest {
  conversationId: string;
  files: PresignFile[];
}

export interface PresignItemDto {
  attachmentId: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
}

export interface PresignResponseDto {
  items: PresignItemDto[];
}

export interface AttachmentUrlsDto {
  thumbUrl: string;
  displayUrl: string;
  expiresAt: string;
}
